import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { startOfISOWeek, getISOWeek, getISOWeekYear } from "date-fns";
import { toast } from "sonner";
import { submitWeekly } from "@/api/burnoff";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";

const weeklySchema = z.object({
  energy: z.number().min(1).max(5),
  stress: z.number().min(1).max(5),
  emotionalState: z.enum([
    "Focused",
    "Motivated",
    "Neutral",
    "Overwhelmed",
    "Drained",
  ]),
  text: z.string().optional(),
});

const TEAM_SLUG = "kutsa";

function getWeekLabel() {
  const now = new Date();
  const monday = startOfISOWeek(now);
  return monday.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
  });
}

function getWeekStorageKey(slug) {
  const now = new Date();
  const year = getISOWeekYear(now);
  const week = getISOWeek(now);
  return `burnoff_weekly_${slug}_${year}-W${String(week).padStart(2, "0")}`;
}

const SCALE = [1, 2, 3, 4, 5];
const ENERGY_BG = {
  1: "bg-[hsl(var(--energy-1))]",
  2: "bg-[hsl(var(--energy-2))]",
  3: "bg-[hsl(var(--energy-3))]",
  4: "bg-[hsl(var(--energy-4))]",
  5: "bg-[hsl(var(--energy-5))]",
};
const ENERGY_BG_SELECTED = {
  1: "bg-[hsl(var(--energy-1-selected))]",
  2: "bg-[hsl(var(--energy-2-selected))]",
  3: "bg-[hsl(var(--energy-3-selected))]",
  4: "bg-[hsl(var(--energy-4-selected))]",
  5: "bg-[hsl(var(--energy-5-selected))]",
};
const ENERGY_TEXT_SELECTED = "text-white";
const ENERGY_TEXT_UNSELECTED = "text-stone-700";

const EMOTIONAL_STATES = [
  "Focused",
  "Motivated",
  "Neutral",
  "Overwhelmed",
  "Drained",
];

function ScaleField({ value, onChange, disabled, ariaLabel }) {
  return (
    <div className="space-y-3 sm:space-y-4">
      <div
        className="flex items-center justify-between gap-2 sm:justify-evenly sm:gap-4"
        role="group"
        aria-label={ariaLabel}
      >
        {SCALE.map((n) => {
          const isSelected = value === n;
          return (
            <button
              key={n}
              type="button"
              disabled={disabled}
              onClick={() => onChange(n)}
              className={`
                flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-base font-medium sm:h-14 sm:w-14 sm:text-lg
                transition-all duration-200 ease-out
                focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-2
                disabled:opacity-60 disabled:pointer-events-none
                hover:scale-105 active:scale-95
                ${isSelected
                  ? `${ENERGY_BG_SELECTED[n]} ${ENERGY_TEXT_SELECTED} shadow-soft`
                  : `${ENERGY_BG[n]} ${ENERGY_TEXT_UNSELECTED} opacity-90 hover:opacity-100`
                }
              `}
            >
              {n}
            </button>
          );
        })}
      </div>
      <p className="flex justify-between gap-2 px-0 text-xs text-stone-400 tracking-tight sm:px-1 sm:text-[0.8125rem]">
        <span className="truncate">Low</span>
        <span className="shrink-0">Neutral</span>
        <span className="truncate text-right">High</span>
      </p>
    </div>
  );
}

export default function WeeklyPage() {
  const storageKey = getWeekStorageKey(TEAM_SLUG);
  const [alreadySubmitted, setAlreadySubmitted] = useState(() =>
    typeof window !== "undefined" ? localStorage.getItem(storageKey) === "true" : false
  );
  const form = useForm({
    resolver: zodResolver(weeklySchema),
    defaultValues: {
      energy: undefined,
      stress: undefined,
      emotionalState: undefined,
      text: "",
    },
  });

  const onSubmit = async (values) => {
    try {
      await submitWeekly(TEAM_SLUG, {
        energy: values.energy,
        stress: values.stress,
        emotionalState: values.emotionalState,
        text: values.text || undefined,
      });
      toast.success("Weekly reflection submitted");
      localStorage.setItem(storageKey, "true");
      setAlreadySubmitted(true);
    } catch {
      toast.error("Submission failed");
    }
  };

  const isSubmitting = form.formState.isSubmitting;
  const disabled = alreadySubmitted;
  const canSubmit =
    form.watch("energy") != null &&
    form.watch("stress") != null &&
    form.watch("emotionalState") != null;

  return (
    <div className="mx-auto w-full min-w-0 max-w-2xl space-y-6 sm:space-y-8">
      <Card className="min-w-0 overflow-hidden border-stone-200/50 bg-card">
        <CardContent className="px-4 py-10 sm:px-8 sm:py-12 md:px-10 md:py-14">
          <p className="mb-2 text-center text-xs text-stone-400">
            Week of {getWeekLabel()}
          </p>

          {disabled && (
            <p className="mb-6 text-center text-[0.9375rem] text-stone-500">
              You've reflected for this week.
            </p>
          )}

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-10"
            >
              <FormField
                control={form.control}
                name="energy"
                render={({ field }) => (
                  <FormItem>
                    <h3 className="text-center text-base font-medium text-stone-800 sm:text-lg">
                      How was your energy this week?
                    </h3>
                    <FormControl>
                      <div className="mt-4">
                        <ScaleField
                          value={field.value}
                          onChange={field.onChange}
                          disabled={disabled}
                          ariaLabel="Energy this week 1 to 5"
                        />
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="stress"
                render={({ field }) => (
                  <FormItem>
                    <h3 className="text-center text-base font-medium text-stone-800 sm:text-lg">
                      How stressed did you feel?
                    </h3>
                    <FormControl>
                      <div className="mt-4">
                        <ScaleField
                          value={field.value}
                          onChange={field.onChange}
                          disabled={disabled}
                          ariaLabel="Stress level 1 to 5"
                        />
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="emotionalState"
                render={({ field }) => (
                  <FormItem>
                    <h3 className="text-center text-base font-medium text-stone-800 sm:text-lg">
                      Which emotion describes your week best?
                    </h3>
                    <FormControl>
                      <div className="mt-4 flex flex-wrap justify-center gap-2">
                        {EMOTIONAL_STATES.map((state) => {
                          const isSelected = field.value === state;
                          return (
                            <button
                              key={state}
                              type="button"
                              disabled={disabled}
                              onClick={() => field.onChange(state)}
                              className={`
                                rounded-full px-4 py-2.5 text-sm font-medium
                                transition-all duration-200
                                focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-2
                                disabled:opacity-60 disabled:pointer-events-none
                                hover:scale-[1.02] active:scale-[0.98]
                                ${isSelected
                                  ? "bg-primary text-primary-foreground shadow-soft"
                                  : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                                }
                              `}
                            >
                              {state}
                            </button>
                          );
                        })}
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="text"
                render={({ field }) => (
                  <FormItem>
                    <h3 className="text-center text-base font-medium text-stone-800 sm:text-lg">
                      Anything you want to reflect on?
                    </h3>
                    <FormControl>
                      <textarea
                        {...field}
                        disabled={disabled}
                        placeholder="Optional..."
                        rows={4}
                        className="mt-4 w-full rounded-xl border border-stone-200/80 bg-stone-50/50 px-4 py-3 text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-300 focus:ring-offset-1 disabled:opacity-60 disabled:cursor-not-allowed resize-y min-h-[100px]"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {!disabled && (
                <Button
                  type="submit"
                  size="lg"
                  className="h-12 w-full text-base font-medium sm:text-[1rem]"
                  disabled={!canSubmit || isSubmitting}
                >
                  {isSubmitting ? "Submitting…" : "Submit"}
                </Button>
              )}
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
